import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { AtlSplashScreenProps } from './props';
import { getGlobalStyleSheets } from '../../global-styles';
import { animations } from '../animations';
import LogoWhite from '@kpmg-uk/tti-design-styles/dist/assets/images/logo-white.svg';
import { SpinnerVariant } from '../spinner';
import { SpinnerAlignment, SpinnerSize, SpinnerType } from '../spinner/props';

import * as SplashScreenTagNames from './splashscreen.tagnames';

@customElement(SplashScreenTagNames.SplashScreen)
export class AtlSplashScreen
   extends LitElement
   implements AtlSplashScreenProps
{
   declare logo?: string;
   declare logoAltText?: string;
   declare hideLogo?: boolean;

   declare appTitle?: string;
   declare copyright?: string;
   declare hideOnShow?: boolean;
   declare show?: boolean;
   declare _displaySlot?: boolean;

   static properties = {
      logo: { type: String },
      logoAltText: { attribute: 'logo-alt-text', type: String },
      hideLogo: { attribute: 'hide-logo', type: Boolean },
      appTitle: { type: String },
      copyright: { type: String },
      hideOnShow: { attribute: 'hide-on-show', type: Boolean },
      show: { type: Boolean },
      _displaySlot: { type: Boolean, state: true },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .text-white {
            font-weight: normal;
         }
      `,
   ];

   @query('.theme-splashscreen')
   private splashScreenElement!: HTMLElement;

   protected override firstUpdated(changedProperties: PropertyValues): void {
      super.firstUpdated(changedProperties);
      this._addAnimation();
   }

   override updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);
      if (changedProperties.has('show')) {
         if (this.hideOnShow && this.show) {
            setTimeout(() => {
               this._hide();
            }, 1500);
         }
      }
   }

   private _addAnimation = () => {
      animations.cssAddFade(this.splashScreenElement, 0.3);
      animations.cssFadeIn(this.splashScreenElement);
   };

   private _hide = () => {
      animations.cssAddFade(this.splashScreenElement, 0.3);
      animations.cssFadeOut(this.splashScreenElement);
      this.splashScreenElement.style.zIndex = '-1';
      this.splashScreenElement.ariaHidden = "true";
      this._displaySlot = true;
   };

   private _renderSlot = () => {
      if (this._displaySlot) {
         return html`<slot></slot>`;
      }
      return nothing;
   };

   render() {
      const logo = this.logo ?? LogoWhite;

      return html`
         <div>
            <div class="theme-splashscreen">
               <div
                  class="h-100 w-100 d-flex py-3 flex-column justify-content-between"
               >
                  <div></div>
                  <div class="text-center">
                     <div class="container">
                        ${!this.hideLogo
            ? html` <div class="row mb-2">
                                <div class="col">
                                   <img
                                      class="logo"
                                      src=${logo}
                                      alt="${this.logoAltText ?? 'KPMG logo'}"
                                   />
                                </div>
                             </div>`
            : nothing}
                        <div class="row mb-4">
                           <div class="col">
                              <h3 class="text-white">
                                 ${this.appTitle || 'KPMG Application'}
                              </h3>
                           </div>
                        </div>
                        <div class="row">
                           <div class="col">
                              <atlantis-spinner
                                 spinner-type=${SpinnerType.Border}
                                 variant=${SpinnerVariant.Light}
                                 size=${SpinnerSize.medium}
                                 align=${SpinnerAlignment.Middle}
                              ></atlantis-spinner>
                           </div>
                        </div>
                     </div>
                  </div>
                  <div class="text-center">
                     <label class="text-white">
                        ${this.copyright ||
         `© ${new Date().getFullYear()} KPMG International Cooperative`}
                     </label>
                  </div>
               </div>
            </div>
            ${this._renderSlot()}
         </div>
      `;
   }
}
